import { monitoring } from 'common/services';

/**
 * ChatV2 (Ably) client telemetry: the event vocabulary and the emit helpers. Every event is a
 * structured Datadog log (monitoring.logEvent — Logs index, flat attributes, no RUM action) so the
 * chat health dashboard can build log-based metrics and SLOs from them: connect success, publish
 * success + ack latency, token refresh outcomes, join (attach → presence → history) timings and
 * delivery lag. Pure strings + helpers — no store or React imports.
 *
 * Attribute convention: flat `chat_*` keys, so they read as clean facets without colliding with the
 * shared logger's `ctx` nesting or Datadog reserved attributes.
 */

export const ChatTelemetry = {
  /** Every Ably connection state change, with the Ably `reason` (e.g. 40142, 80003) when present. */
  connectionState: 'broadcast.chat.connection_state',
  /** Every room channel state change (attach / detach / suspend / fail), with the reason. */
  channelState: 'broadcast.chat.channel_state',
  /** Outcome + per-step timings of joining the room: attach → presence enter → history backfill. */
  join: 'broadcast.chat.join',
  /** Outcome + ack round-trip of every user-initiated write (messages, reactions, hands, deletes). */
  publish: 'broadcast.chat.publish',
  /** Outcome + HTTP status of a chat token request (backend pull fallback or chat-service refresh). */
  token: 'broadcast.chat.token',
  /** Periodic per-client summary of inbound delivery lag (receive time − Ably server timestamp). */
  delivery: 'broadcast.chat.delivery',
} as const;

export type ChatPublishKind =
  | 'message'
  | 'room_reaction'
  | 'lower_participant_hand'
  | 'hand_update'
  | 'reaction_add'
  | 'reaction_remove'
  | 'message_delete';

export type ChatTokenSource = 'pull' | 'refresh';

export type ChatOutcome = 'success' | 'failure';

export type ChatJoinStep = 'attach' | 'presence' | 'history';

/** How often the delivery-lag window is flushed. One log per client per window keeps volume bounded. */
export const CHAT_DELIVERY_FLUSH_INTERVAL_MS = 60_000;

/**
 * Pull the Ably `ErrorInfo` fields out of an unknown rejection. Ably rejects with an `ErrorInfo`
 * (`code` like 40160 / 42910 / 80003, plus an HTTP-style `statusCode`); anything else gets neither.
 */
export const chatErrorAttrs = (error: unknown): { chat_error_code?: number; chat_error_status?: number } => {
  if (typeof error !== 'object' || error === null) return {};
  const { code, statusCode } = error as { code?: unknown; statusCode?: unknown };
  return {
    ...(typeof code === 'number' ? { chat_error_code: code } : {}),
    ...(typeof statusCode === 'number' ? { chat_error_status: statusCode } : {}),
  };
};

/**
 * Emit one chat telemetry event. Telemetry never decides whether the chat work succeeded, so a throw
 * from the logging SDK is swallowed here rather than surfacing in Ably callbacks or promise chains.
 */
export const trackChatEvent = (
  event: (typeof ChatTelemetry)[keyof typeof ChatTelemetry],
  attrs: Record<string, unknown>
) => {
  try {
    monitoring.logEvent(event, attrs);
  } catch {
    // Reporting path: nothing useful to do if the logs SDK itself fails.
  }
};

const percentile = (sorted: number[], p: number): number =>
  sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];

/**
 * Accumulates inbound delivery-lag samples and summarises them on flush. Lag is receive time minus
 * Ably's server timestamp. A client clock running behind Ably's is common and would clamp every
 * sample to 0, so the receive time is corrected by the measured client↔Ably clock offset once known
 * ({@link setClockOffset}); until then samples are uncorrected and flagged `chat_clock_corrected: false`.
 */
export class ChatDeliveryLagWindow {
  private samples: number[] = [];

  private clockOffsetMs: number | undefined;

  /** Ably server time minus client time, from `Realtime.time()` (see BroadcastChatConnection). */
  setClockOffset(offsetMs: number): void {
    this.clockOffsetMs = offsetMs;
  }

  record(serverTimestamp: number | undefined, now: number = Date.now()): void {
    if (typeof serverTimestamp !== 'number' || !Number.isFinite(serverTimestamp)) return;
    this.samples.push(Math.max(0, now + (this.clockOffsetMs ?? 0) - serverTimestamp));
  }

  /** Emit the window as one summary event (if anything was received) and reset it. */
  flush(attrs: Record<string, unknown>): void {
    if (!this.samples.length) return;
    const sorted = [...this.samples].sort((a, b) => a - b);
    this.samples = [];
    trackChatEvent(ChatTelemetry.delivery, {
      ...attrs,
      chat_received_count: sorted.length,
      chat_lag_p50_ms: percentile(sorted, 50),
      chat_lag_p95_ms: percentile(sorted, 95),
      chat_lag_max_ms: sorted[sorted.length - 1],
      chat_clock_corrected: this.clockOffsetMs !== undefined,
      chat_clock_offset_ms: this.clockOffsetMs === undefined ? undefined : Math.round(this.clockOffsetMs),
    });
  }
}
